/**
 * Multi-tenant SaaS mode (§6.6).
 * Extract the org slug from the current hostname and use it to key API calls / branding.
 *
 * Two runtime shapes are supported:
 *  1. Subdomain routing (production)   — `acme.hrms.dtable.io` → slug=acme
 *  2. Path-prefix (dev + single-tenant) — `/acme/dashboard`    → falls back to `null`
 *
 * The backend AuthController already resolves org from the JWT payload; this is a
 * *hint* for the login page (which tenant is asking for creds) and for injecting
 * a per-tenant CSS theme before the JWT exists.
 */

const HOST_SUFFIXES = [
  '.hrms.dtable.io',
  '.dtable.io',
  '.dtablehrms.com',
];

export function getTenantSlug(): string | null {
  if (typeof window === 'undefined') return null;

  const host = window.location.hostname;

  // Localhost / IP / bare hostname → no subdomain tenant
  if (host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) {
    return null;
  }

  for (const suffix of HOST_SUFFIXES) {
    if (host.endsWith(suffix)) {
      const slug = host.slice(0, host.length - suffix.length);
      if (slug && !slug.includes('.')) return slug;
    }
  }

  return null;
}

export interface TenantBrand {
  primary?: string;
  primaryDark?: string;
  primaryLight?: string;
  accent?: string;
  logoUrl?: string | null;
}

/**
 * Apply per-tenant brand at runtime by updating CSS custom properties.
 * Falls back to the D-Table default theme if no override is set.
 */
export function applyTenantBrand(brand: TenantBrand | null | undefined): void {
  if (typeof document === 'undefined' || !brand) return;
  const root = document.documentElement;

  if (brand.primary) root.style.setProperty('--db-primary', brand.primary);
  if (brand.primaryDark) root.style.setProperty('--db-primary-dark', brand.primaryDark);
  if (brand.primaryLight) root.style.setProperty('--db-primary-light', brand.primaryLight);
  if (brand.accent) root.style.setProperty('--db-accent', brand.accent);
}
